import { CartUpdatedEvent } from '@theme/events';

/**
 * Mini buybox du volet burger (panneau « Votre format »).
 *
 * Les prix par palier sont précalculés côté Liquid (filtre money) et embarqués
 * dans un JSON : le JS ne formate jamais d'argent, car Theme.trailingZeros = false
 * ferait perdre les décimales (« 1,00 € » deviendrait « 1 € »).
 */
class HeaderDrawerBuybox extends HTMLElement {
  constructor() {
    super();
    this.tiers = [];
    this.loading = false;
  }

  connectedCallback() {
    const tiersScript = this.querySelector('[data-ref="buybox-tiers"]');
    try {
      this.tiers = tiersScript ? JSON.parse(tiersScript.textContent) : [];
    } catch (error) {
      this.tiers = [];
    }

    this.form = this.querySelector('form');
    this.qtyInput = this.querySelector('[data-ref="buybox-qty"]');
    this.priceEl = this.querySelector('[data-ref="buybox-price"]');
    this.subEl = this.querySelector('[data-ref="buybox-sub"]');
    this.unitEl = this.querySelector('[data-ref="buybox-unit"]');
    this.offerRows = Array.from(this.querySelectorAll('[data-ref="offer-row"]'));
    this.submitBtn = this.querySelector('[data-ref="buybox-submit"]');
    this.errorEl = this.querySelector('[data-ref="buybox-error"]');

    // L'évènement change du quantity-selector ne bulle pas : liaison directe sur l'input.
    this.qtyInput?.addEventListener('change', this.#onQtyChange);
    this.form?.addEventListener('submit', this.#onSubmit);

    this.#render(this.#currentQty());
  }

  disconnectedCallback() {
    this.qtyInput?.removeEventListener('change', this.#onQtyChange);
    this.form?.removeEventListener('submit', this.#onSubmit);
  }

  #currentQty() {
    const qty = parseInt(this.qtyInput?.value, 10);
    return Number.isFinite(qty) && qty > 0 ? qty : 1;
  }

  #onQtyChange = () => {
    this.#render(this.#currentQty());
  };

  /**
   * Met à jour prix CTA, sous-ligne, prix par portion et l'état des lignes
   * d'offres pour la quantité courante.
   */
  #render(qty) {
    const tier = this.tiers.find((t) => t.qty === qty) || this.tiers[this.tiers.length - 1];
    if (!tier) return;

    if (this.priceEl) this.priceEl.textContent = tier.priceLabel;
    if (this.subEl) this.subEl.textContent = tier.subLabel;
    if (this.unitEl) this.unitEl.textContent = tier.unitLabel;

    this.offerRows.forEach((row) => {
      const active = qty >= parseInt(row.dataset.threshold, 10);
      row.classList.toggle('is-active', active);
      const state = row.querySelector('[data-ref="offer-state"]');
      if (state) state.textContent = active ? row.dataset.activeText : row.dataset.inactiveText;
    });
  }

  #onSubmit = async (event) => {
    event.preventDefault();
    if (this.loading || !this.submitBtn) return;

    const idInput = this.form?.querySelector('input[name="id"]');
    const variantId = parseInt(idInput?.value, 10);
    const quantity = this.#currentQty();
    if (!Number.isFinite(variantId)) return;

    this.loading = true;
    this.submitBtn.classList.add('is-loading');
    if (this.errorEl) this.errorEl.hidden = true;

    // Même contrat que product-form.js : demander le re-rendu des sections
    // auto-gifts + cart drawer pour que les cadeaux se déclenchent.
    const sections = ['auto-gifts'];
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) sections.push(cartDrawer.dataset.sectionId);

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: quantity }],
          sections: sections.join(','),
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        this.#showError(data.description);
        return;
      }

      const currentCount = document.querySelector('cart-icon')?.getCurrentItemCount() || 0;

      // Fermer le volet menu D'ABORD : son timeout interne (125 ms) retire
      // overflow-hidden du body. On dispatch cart:updated à 180 ms pour que
      // l'ouverture du cart drawer repose le verrou de scroll APRES ce retrait.
      this.closest('header-component')?.closeMobileMenu();
      setTimeout(() => {
        document.dispatchEvent(
          new CartUpdatedEvent(
            {},
            {
              itemCount: currentCount + quantity,
              source: 'header-drawer-buybox',
              sections: data.sections,
            }
          )
        );
      }, 180);
    } catch (error) {
      this.#showError();
    } finally {
      this.loading = false;
      this.submitBtn.classList.remove('is-loading');
    }
  };

  #showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || this.dataset.errorMessage || 'Erreur';
    this.errorEl.hidden = false;
  }
}

if (!customElements.get('header-drawer-buybox')) {
  customElements.define('header-drawer-buybox', HeaderDrawerBuybox);
}
